/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectAssign: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [verifiedNGOs, setVerifiedNGOs] = useState<any[]>([]);
  const [selectedNGOs, setSelectedNGOs] = useState<string[]>([]);

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadVerifiedNGOs();
    }
  }, [projectId, companyId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      setProject(data);
      // Load already assigned NGOs
      const { data: assigned } = await supabase
        .from('project_ngos')
        .select('ngo_id')
        .eq('project_id', projectId);
      if (assigned) {
        setSelectedNGOs(assigned.map((a: any) => a.ngo_id));
      }
    }
  };

  const loadVerifiedNGOs = async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from('ngos')
      .select('*')
      .eq('company_id', companyId)
      .eq('status', 'verified')
      .order('legal_name', { ascending: true });

    if (!error && data) {
      setVerifiedNGOs(data);
    }
  };

  const handleToggleNGO = (ngoId: string) => {
    setSelectedNGOs(prev =>
      prev.includes(ngoId)
        ? prev.filter(id => id !== ngoId)
        : [...prev, ngoId]
    );
  };

  const handleAssign = async () => {
    if (selectedNGOs.length === 0) {
      setError('Please select at least one NGO');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !projectId) throw new Error('Not authenticated');

      // Remove existing assignments
      await supabase
        .from('project_ngos')
        .delete()
        .eq('project_id', projectId);

      // Create new assignments
      for (const ngoId of selectedNGOs) {
        const { error: assignError } = await supabase
          .from('project_ngos')
          .insert({
            project_id: projectId,
            ngo_id: ngoId,
            status: 'pending',
            assigned_by: user.id
          });

        if (assignError) throw assignError;
      }

      // Update project status to 'assigned'
      const { error: updateError } = await supabase
        .from('projects')
        .update({ status: 'assigned' })
        .eq('id', projectId);

      if (updateError) throw updateError;

      navigate(`/client/company/projects/${projectId}`);
    } catch (err: any) {
      setError(err.message || 'Failed to assign NGOs');
    } finally {
      setLoading(false);
    }
  };

  if (!project) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-section">
        <h1>Assign NGOs to Project</h1>
        <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
          Select verified NGOs to assign to: <strong>{project.name}</strong>
        </p>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {verifiedNGOs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <div className="empty-state-title">No Verified NGOs Available</div>
            <div className="empty-state-description">
              You need to verify at least one NGO before assigning projects.
              <br />
              <a href={`/client/company/ngos/invite`}>Invite and verify NGOs</a>
            </div>
          </div>
        ) : (
          <>
            <div className="card">
              <h2>Select NGOs</h2>
              <div className="ngo-list">
                {verifiedNGOs.map((ngo) => (
                  <div
                    key={ngo.id}
                    className={`ngo-item ${selectedNGOs.includes(ngo.id) ? 'selected' : ''}`}
                    onClick={() => handleToggleNGO(ngo.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedNGOs.includes(ngo.id)}
                      readOnly
                    />
                    <div className="ngo-item-info">
                      <strong>{ngo.legal_name}</strong>
                      <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--muted)' }}>
                        {ngo.registration_type} • {ngo.contact?.email}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => navigate(`/client/company/projects/${projectId}`)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAssign}
                disabled={loading || selectedNGOs.length === 0}
              >
                {loading ? 'Assigning...' : `Assign ${selectedNGOs.length} NGO(s)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectAssign;


